import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  firstValueFrom,
  of,
  tap,
  throwError,
} from 'rxjs';
import { ApiService } from './api.service';
import { TokenService } from './token.service';
import type { UserProfile } from '@core/models/user.model';
import type {
  AuthResponse,
  AuthTokens,
  ForgotPasswordPayload,
  LoginPayload,
  RegisterSchoolPayload,
  RegisterSchoolResponse,
  ResetPasswordPayload,
} from '@core/models/auth.model';

const REMEMBERED_SLUG_KEY = 'shiksha_web.tenant_slug';
const SESSION_SLUG_KEY = 'shiksha_web.session_slug';

/**
 * Central auth state + API calls.
 *
 * - Tokens live in TokenService (memory only).
 * - `currentUser` is exposed as both a signal (for templates/guards) and a
 *   BehaviorSubject (`currentUser$`) for reactive consumers.
 * - The only persisted value is the tenant slug for "remember me" (NOT a secret).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly tokens = inject(TokenService);
  private readonly router = inject(Router);

  private readonly _currentUser = signal<UserProfile | null>(null);
  readonly currentUser = this._currentUser.asReadonly();
  readonly isLoggedIn = computed(() => this._currentUser() !== null);

  /** True when the signed-in principal is a platform super_admin. */
  readonly isSuperAdmin = computed(() =>
    (this._currentUser()?.roles ?? []).includes('super_admin'),
  );

  /** A seeded super_admin must change their password on first login. */
  private readonly _mustChangePassword = signal(false);
  readonly mustChangePassword = this._mustChangePassword.asReadonly();

  // The tenant (school) the current session belongs to — for display.
  private readonly _tenantSlug = signal<string | null>(null);
  readonly tenantSlug = this._tenantSlug.asReadonly();

  private readonly currentUserSubject = new BehaviorSubject<UserProfile | null>(
    null,
  );
  readonly currentUser$ = this.currentUserSubject.asObservable();

  // ── Auth flows ─────────────────────────────────────────────────────────────

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.api.post<AuthResponse>('/auth/login', payload).pipe(
      tap((res) => {
        this.setSession(res.user, res.tokens);
        this._mustChangePassword.set(res.mustChangePassword ?? false);
        // Super_admin has no tenant slug to remember.
        if (payload.tenantSlug) {
          this.setSessionSlug(payload.tenantSlug);
        }
      }),
    );
  }

  register(payload: RegisterSchoolPayload): Observable<RegisterSchoolResponse> {
    return this.api.post<RegisterSchoolResponse>(
      '/auth/register-school',
      payload,
    ).pipe(
      tap((res) => {
        this.setSession(res.user, res.tokens);
        this.setSessionSlug(res.tenant.slug);
      }),
    );
  }

  /**
   * Restore a session on app startup from the persisted refresh token: rotate
   * tokens, then load the user profile. Called by the APP_INITIALIZER before the
   * router runs so guards see an accurate `isLoggedIn`. Returns false (and clears
   * any stale token) if there's no valid session to restore.
   */
  async tryAutoLogin(): Promise<boolean> {
    if (!this.tokens.hasPersistedSession()) {
      return false;
    }
    try {
      await firstValueFrom(this.refresh()); // uses the persisted refresh token
      const user = await firstValueFrom(this.me());
      this.setUser(user);
      const slug = this.readSessionSlug();
      if (slug) {
        this._tenantSlug.set(slug);
      }
      return true;
    } catch {
      this.tokens.clear();
      this.clearSessionSlug();
      return false;
    }
  }

  forgotPassword(payload: ForgotPasswordPayload): Observable<unknown> {
    return this.api.post<unknown>('/auth/forgot-password', payload);
  }

  resetPassword(payload: ResetPasswordPayload): Observable<unknown> {
    return this.api.post<unknown>('/auth/reset-password', payload);
  }

  me(): Observable<UserProfile> {
    return this.api
      .get<UserProfile>('/auth/me')
      .pipe(tap((user) => this.setUser(user)));
  }

  /** Rotate tokens using the in-memory refresh token. Used by the interceptor. */
  refresh(): Observable<AuthTokens> {
    const refreshToken = this.tokens.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }
    return this.api
      .post<AuthTokens>('/auth/refresh', { refreshToken })
      .pipe(
        tap((tokens) =>
          this.tokens.setTokens(tokens.accessToken, tokens.refreshToken),
        ),
      );
  }

  /** Best-effort server logout, then always clear local state. */
  logout(): void {
    this.api
      .post('/auth/logout', {})
      .pipe(catchError(() => of(null)))
      .subscribe(() => this.completeLogout());
  }

  /** Immediate local logout (e.g. when a token refresh fails). */
  forceLogout(): void {
    this.completeLogout();
  }

  // ── Remember-me (tenant slug only — non-secret) ─────────────────────────────

  getRememberedSlug(): string | null {
    return localStorage.getItem(REMEMBERED_SLUG_KEY);
  }

  setRememberedSlug(slug: string | null): void {
    if (slug) {
      localStorage.setItem(REMEMBERED_SLUG_KEY, slug);
    } else {
      localStorage.removeItem(REMEMBERED_SLUG_KEY);
    }
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  private setSession(user: UserProfile, tokens: AuthTokens): void {
    this.tokens.setTokens(tokens.accessToken, tokens.refreshToken);
    this.setUser(user);
  }

  private setUser(user: UserProfile): void {
    this._currentUser.set(user);
    this.currentUserSubject.next(user);
  }

  private completeLogout(): void {
    this.tokens.clear();
    this.clearSessionSlug();
    this._currentUser.set(null);
    this._tenantSlug.set(null);
    this.currentUserSubject.next(null);
    void this.router.navigate(['/login']);
  }

  /** Persist the (non-secret) tenant slug so it can be shown after a reload. */
  private setSessionSlug(slug: string): void {
    this._tenantSlug.set(slug);
    try {
      localStorage.setItem(SESSION_SLUG_KEY, slug);
    } catch {
      /* storage unavailable */
    }
  }

  private readSessionSlug(): string | null {
    try {
      return localStorage.getItem(SESSION_SLUG_KEY);
    } catch {
      return null;
    }
  }

  private clearSessionSlug(): void {
    try {
      localStorage.removeItem(SESSION_SLUG_KEY);
    } catch {
      /* storage unavailable */
    }
  }
}
